import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { SubscriptionAccessReferenceSchema } from '@vendepay/contracts';
import { authenticatePayment } from './stripe-action';
import {
  cancelSubscriptionByToken,
  openSubscriptionSession,
  requestSubscriptionLink,
  isSubscriptionAccessError,
  confirmSubscriptionInvoice,
  fetchSubscriptionCardUpdateStatus,
  fetchSubscriptionAction,
  paySubscriptionInvoice,
  type SubscriptionActionKind,
  type SubscriptionActionView,
  updateSubscriptionCard,
} from './api';
import { formatMoney } from './money';
import { StripeCardPanel, type StripeCardHandle } from './StripeCardSection';
import { CheckoutErrorAlert } from './CheckoutErrorAlert';
import type { CheckoutErrorDescriptor } from './errorPresentation';
import { presentLocalizedError } from './errorPresentation';
import { isPendingSettlement } from './subscription-action-settlement';
import { checkoutI18n, currentLocale, currentLocaleTag, localeDirection, normalizeLocale, tr, useCurrentLocale } from '../i18n';
import './checkout.css';

type CancellationState = {
  status: 'cancellation';
  outcome: 'canceled' | 'scheduled';
  view: SubscriptionActionView;
};

type LoadState =
  | { status: 'loading' }
  | { status: 'access-expired'; processing: boolean; sent?: boolean; error?: CheckoutErrorDescriptor }
  | { status: 'error'; error: CheckoutErrorDescriptor }
  | { status: 'processing'; action: 'pay-now'; accessId: string }
  | CancellationState
  | {
      status: 'ready';
      action: SubscriptionActionKind;
      accessId: string;
      view: SubscriptionActionView;
      error?: CheckoutErrorDescriptor;
    }
  | { status: 'done'; action: 'pay-now' | 'update-card' };

export function SubscriptionActionPage() {
  const locale = useCurrentLocale();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const action = normalizeAction(params.get('action'));
  const accessId = params.get('accessId') ?? '';
  const urlLegacyToken = params.get('token');
  const code = useRef<string | undefined>(undefined);
  const codeAccessId = useRef(accessId);
  const legacyEntry = useRef<{ action: SubscriptionActionKind | null; accessId: string; token: string } | undefined>(undefined);
  const entryNavigation = useRef<string | undefined>(undefined);
  const entryVersion = useRef(0);
  const pending = useRef<{ key: string; promise: Promise<SubscriptionActionView>; hasCode: boolean; settled: boolean } | undefined>(undefined);
  const [reload, setReload] = useState(0);
  const resendId = useRef<string | undefined>(undefined);
  const cardRef = useRef<StripeCardHandle>(null);
  const requestVersion = useRef(0);
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  const [cardholderName, setCardholderName] = useState('');

  useEffect(() => {
    const previousLocale = currentLocale();
    const referrer = document.createElement('meta');
    referrer.name = 'referrer';
    referrer.content = 'no-referrer';
    document.head.appendChild(referrer);
    void checkoutI18n.changeLanguage('en');
    return () => { referrer.remove(); void checkoutI18n.changeLanguage(previousLocale); };
  }, []);

  useEffect(() => {
    let canceled = false;
    requestVersion.current += 1;
    setSubmitting(false);
    resendId.current = undefined;
    if (location.hash.length > 0 || urlLegacyToken !== null) {
      if (entryNavigation.current !== location.key) {
        entryNavigation.current = location.key;
        const opening = pending.current;
        if (!opening || opening.settled || !opening.hasCode) entryVersion.current += 1;
      }
    } else entryNavigation.current = undefined;
    if (urlLegacyToken !== null) legacyEntry.current = { action, accessId, token: urlLegacyToken };
    else if (legacyEntry.current !== undefined && (legacyEntry.current.action !== action || legacyEntry.current.accessId !== accessId)) legacyEntry.current = undefined;
    const legacy = legacyEntry.current?.token;
    if (codeAccessId.current !== accessId) { code.current = undefined; codeAccessId.current = accessId; }
    const fragmentCode = new URLSearchParams(location.hash.slice(1)).get('code');
    if (fragmentCode) code.current = fragmentCode;
    if (urlLegacyToken !== null || location.hash.length > 0) {
      const clean = new URLSearchParams();
      if (action !== null) clean.set('action', action);
      if (accessId) clean.set('accessId', accessId);
      setParams(clean, { replace: true });
    }
    if (action === null) {
      setState({ status: 'error', error: presentLocalizedError({ key: 'error.invalidActionLink', kind: 'validation' }) });
      return;
    }
    if (legacy !== undefined) {
      void crypto.subtle.digest('SHA-256', new TextEncoder().encode(legacy)).then(hash => {
        if (canceled) return;
        const digest = Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join('');
        legacyEntry.current = undefined;
        setParams({ action, accessId: `legacy_${digest}` }, { replace: true });
      }).catch(() => {
        if (!canceled) setState({ status: 'error', error: presentLocalizedError({ key: 'error.invalidActionLink', kind: 'validation' }) });
      });
      return () => { canceled = true; };
    }
    if (accessId.startsWith('legacy_')) {
      setState({ status: 'access-expired', processing: false });
      return;
    }
    if (action === null || !SubscriptionAccessReferenceSchema.safeParse(accessId).success) {
      setState({ status: 'error', error: presentLocalizedError({ key: 'error.invalidActionLink', kind: 'validation' }) });
      return;
    }
    setState({ status: 'loading' });
    const key = `${action}:${accessId}:${reload}:${entryVersion.current}`;
    if (pending.current?.key !== key) {
      const entryCode = code.current;
      const load = async () => {
        try {
          return await fetchSubscriptionAction(action, accessId);
        } catch (error) {
          if (!isSubscriptionAccessError(error) || !entryCode) throw error;
        }
        try {
          await openSubscriptionSession(accessId, entryCode!);
          if (code.current === entryCode) code.current = undefined;
        } catch (error) {
          try { return await fetchSubscriptionAction(action, accessId); } catch { throw error; }
        }
        return fetchSubscriptionAction(action, accessId);
      };
      const request = { key, promise: load(), hasCode: entryCode !== undefined, settled: false };
      request.promise = request.promise.finally(() => { request.settled = true; });
      pending.current = request;
    }
    pending.current.promise.then(view => {
      if (!canceled) {
        void checkoutI18n.changeLanguage(normalizeLocale(view.buyerLocale) ?? 'en');
        const cancellation = action === 'cancel' ? cancellationState(view) : undefined;
        setState(cancellation ?? { status: 'ready', action, accessId, view });
      }
    }).catch(error => {
      if (!canceled) {
        setState(isSubscriptionAccessError(error)
          ? { status: 'access-expired', processing: false }
          : { status: 'error', error: presentLocalizedError({ key: 'error.subscriptionActionLoad', kind: 'system', action: 'retry' }) });
      }
    });
    return () => { canceled = true; requestVersion.current += 1; };
  }, [action, accessId, reload, location.hash, location.key, urlLegacyToken]);

  useEffect(() => {
    if (state.status !== 'processing') return;
    let canceled = false;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const view = await fetchSubscriptionAction('pay-now', state.accessId);
        if (!canceled && view.invoice?.status === 'PAID') {
          setState({ status: 'done', action: 'pay-now' });
          return;
        }
      } catch (error) {
        if (isSubscriptionAccessError(error)) {
          if (!canceled) setState({ status: 'access-expired', processing: true });
          return;
        }
      }
      if (!canceled) {
        timeout = setTimeout(() => void poll(), 2_000);
      }
    };
    timeout = setTimeout(() => void poll(), 2_000);
    return () => {
      canceled = true;
      if (timeout !== undefined) clearTimeout(timeout);
    };
  }, [state]);

  const title = useMemo(() => {
    if (state.status === 'cancellation') {
      return tr(state.outcome === 'canceled' ? 'action.alreadyCanceledTitle' : 'action.canceledTitle');
    }
    if (state.status === 'processing') {
      return tr('action.processingTitle');
    }
    if (state.status === 'done') {
      if (state.action === 'update-card') return tr('action.cardUpdatedTitle');
      return tr('action.paidTitle');
    }
    if (state.status !== 'ready') {
      return tr('subscription.single');
    }
    if (state.action === 'cancel') return tr('action.cancelTitle');
    if (state.action === 'update-card') return tr('action.cardTitle');
    return tr('action.payTitle');
  }, [state, locale]);
  const paymentRequiresAction =
    state.status === 'ready' && state.action === 'pay-now' && state.view.invoice?.status === 'AWAITING_ACTION';

  async function submit() {
    if (state.status !== 'ready') return;
    const version = requestVersion.current;
    setSubmitting(true);
    try {
      if (state.action === 'cancel') {
        const result = await cancelSubscriptionByToken(state.accessId, true);
        if (version !== requestVersion.current) return;
        setState(cancellationState(result) ?? {
          status: 'ready',
          action: state.action,
          accessId: state.accessId,
          view: result,
          error: presentLocalizedError({ key: 'error.subscriptionAction', kind: 'system', action: 'retry' }),
        });
        return;
      } else if (state.action === 'update-card') {
        const updateId = makeUpdateId();
        const tokenResult = await cardRef.current?.createPaymentMethod();
        if (!tokenResult || 'error' in tokenResult) {
          setState({
            status: 'ready',
            action: state.action,
            accessId: state.accessId,
            view: state.view,
            error: tokenResult?.error ?? presentLocalizedError({ key: 'error.cardDetails', kind: 'payment' }),
          });
          return;
        }
        const result = await updateSubscriptionCard(state.accessId, updateId, tokenResult.token);
        if (result.payment?.requiresAction === true) {
          const actionResult = await cardRef.current?.confirmAction(result.payment.clientSecret ?? '');
          if (!actionResult || 'error' in actionResult) {
            setState({
              status: 'ready',
              action: state.action,
              accessId: state.accessId,
              view: state.view,
              error: actionResult?.error ?? presentLocalizedError({ key: 'error.subscriptionAuthentication', kind: 'payment' }),
            });
            return;
          }
        } else if (result.payment?.status === 'FAILED') {
          setState({
            status: 'ready',
            action: state.action,
            accessId: state.accessId,
            view: result,
            error: presentLocalizedError({ key: 'error.subscriptionAction', kind: 'payment', action: 'retry' }),
          });
          return;
        }
        const confirmed = await pollCardUpdate(state.accessId, updateId);
        if (confirmed.payment?.status !== 'SUCCEEDED') {
          setState({
            status: 'ready',
            action: state.action,
            accessId: state.accessId,
            view: confirmed,
            error: presentLocalizedError({ key: 'error.subscriptionAction', kind: 'payment', action: 'retry' }),
          });
          return;
        }
      } else {
        const result = await paySubscriptionInvoice(state.accessId);
        if (result.payment?.requiresAction === true) {
          await authenticatePayment(result.payment ?? {});
          let confirmed = await confirmSubscriptionInvoice(state.accessId);
          if (isPendingSettlement(confirmed)) {
            confirmed = await pollInvoiceSettlement(state.accessId, confirmed);
            if (confirmed.invoice?.status !== 'PAID') {
              setState({ status: 'processing', action: 'pay-now', accessId: state.accessId });
              return;
            }
          }
          if (confirmed.payment?.requiresAction === true || confirmed.invoice?.status !== 'PAID') {
            setState({
              status: 'ready',
              action: state.action,
              accessId: state.accessId,
              view: confirmed,
              error: presentLocalizedError({ key: 'error.subscriptionAction', kind: 'payment', action: 'retry' }),
            });
            return;
          }
        } else if (isPendingSettlement(result)) {
          const settled = await pollInvoiceSettlement(state.accessId, result);
          if (settled.invoice?.status !== 'PAID') {
            setState({ status: 'processing', action: 'pay-now', accessId: state.accessId });
            return;
          }
        } else if (result.invoice?.status !== 'PAID') {
          setState({
            status: 'ready',
            action: state.action,
            accessId: state.accessId,
            view: result,
            error: presentLocalizedError({ key: 'error.subscriptionAction', kind: 'payment', action: 'retry' }),
          });
          return;
        }
      }
      setState({ status: 'done', action: state.action });
    } catch (error) {
      if (version !== requestVersion.current) return;
      if (state.action === 'cancel') {
        let accessExpired = false;
        const latest = await fetchSubscriptionAction('cancel', state.accessId).catch(readError => {
          accessExpired = isSubscriptionAccessError(readError);
          return undefined;
        });
        if (version !== requestVersion.current) return;
        const cancellation = latest === undefined ? undefined : cancellationState(latest);
        if (cancellation !== undefined) {
          setState(cancellation);
          return;
        }
        if (accessExpired) {
          setState({ status: 'access-expired', processing: false });
          return;
        }
      }
      if (isSubscriptionAccessError(error)) {
        setState({ status: 'access-expired', processing: state.action === 'pay-now' });
        return;
      }
      setState({
        status: 'ready',
        action: state.action,
        accessId: state.accessId,
        view: state.view,
        error: presentLocalizedError({ key: 'error.subscriptionAction', kind: 'system', action: 'retry' }),
      });
    } finally {
      if (state.action !== 'cancel' || version === requestVersion.current) setSubmitting(false);
    }
  }

  async function resend() {
    if (action === null || state.status !== 'access-expired') return;
    const version = requestVersion.current;
    setSubmitting(true);
    try {
      resendId.current ??= crypto.randomUUID();
      await requestSubscriptionLink(accessId, action, resendId.current);
      if (version !== requestVersion.current) return;
      setState({ status: 'access-expired', processing: state.processing, sent: true });
    } catch {
      if (version !== requestVersion.current) return;
      setState({ ...state, error: presentLocalizedError({ key: 'error.subscriptionAction', kind: 'system', action: 'retry' }) });
    } finally { if (version === requestVersion.current) setSubmitting(false); }
  }

  return (
    <main className="vp-root vp-action-page" lang={currentLocaleTag()} dir={localeDirection(locale)}>
      <section className="vp-action-panel" aria-live="polite">
        <div className="vp-action-brand">VendePay</div>
        <h1>{title}</h1>
        {state.status === 'cancellation' ? (
          <p className="vp-muted">
            {tr(state.outcome === 'canceled' ? 'action.alreadyCanceledBody' : 'action.canceledBody')}
          </p>
        ) : null}
        {state.status === 'loading' ? <p className="vp-muted">{tr('action.loading')}</p> : null}
        {state.status === 'error' ? (
          <CheckoutErrorAlert
            error={state.error}
            placement="page"
            autoFocus
            onAction={() => setReload(value => value + 1)}
          />
        ) : null}
        {state.status === 'access-expired' ? (
          <>
            <p className="vp-muted">{tr('action.accessExpired')}</p>
            {state.processing ? <p className="vp-muted">{tr('action.accessProcessing')}</p> : null}
            {state.error ? <CheckoutErrorAlert error={state.error} placement="page" onAction={() => void resend()} /> : null}
            {state.sent ? <p role="status" className="vp-muted">{tr('action.linkSent')}</p> : (
              <button className="vp-action-button" type="button" onClick={() => void resend()} disabled={submitting || action === null}>
                {submitting ? tr('common.processing') : tr('action.requestLink')}
              </button>
            )}
          </>
        ) : null}
        {state.status === 'done' ? (
          <p className="vp-muted">
            {state.action === 'update-card'
              ? tr('action.cardUpdatedBody')
              : tr('action.paidBody')}
          </p>
        ) : null}
        {state.status === 'processing' ? (
          <p className="vp-muted">
            {tr('action.processingBody')}
          </p>
        ) : null}
        {state.status === 'ready' || state.status === 'cancellation' ? (
          <>
            {state.status === 'ready' && state.error && <CheckoutErrorAlert error={state.error} placement="toast" onAction={() => void submit()} autoFocus />}
            <dl className="vp-action-summary">
              <div>
                <dt>{tr('action.plan')}</dt>
                <dd>{state.view.subscription.planName ?? tr('subscription.single')}</dd>
              </div>
              <div>
                <dt>{tr('action.status')}</dt>
                <dd>{subscriptionStatusLabel(state.view.subscription.status)}</dd>
              </div>
              {state.view.buyerEmailMasked ? (
                <div>
                  <dt>{tr('field.email')}</dt>
                  <dd>{state.view.buyerEmailMasked}</dd>
                </div>
              ) : null}
              {state.view.invoice ? (
                <div>
                  <dt>{tr('action.invoice')}</dt>
                  <dd>
                    {formatMoney(state.view.invoice.value)} · {invoiceStatusLabel(state.view.invoice.status)}
                  </dd>
                </div>
              ) : null}
              {state.status === 'ready' && state.action === 'update-card' && state.view.subscription.chargeAmount ? (
                <div>
                  <dt>{tr('action.chargeAmount')}</dt>
                  <dd>{formatMoney(state.view.subscription.chargeAmount)}</dd>
                </div>
              ) : null}
              <div>
                <dt>{tr('action.period')}</dt>
                <dd>{tr('action.until', { date: formatDate(state.view.subscription.currentPeriodEndAt) })}</dd>
              </div>
            </dl>
          </>
        ) : null}
        {state.status === 'ready' ? (
          <>
            {paymentRequiresAction ? <p className="vp-muted">{tr('action.authentication')}</p> : null}
            {state.action === 'update-card' ? (
              state.view.payment?.publishableKey ? (
                <StripeCardPanel
                  ref={cardRef}
                  publishableKey={state.view.payment.publishableKey}
                  cardholderName={cardholderName}
                  onCardholderNameChange={setCardholderName}
                  showSecurity
                />
              ) : (
                <CheckoutErrorAlert
                  error={presentLocalizedError({ key: 'error.subscriptionCardForm', kind: 'system', action: 'retry' })}
                  placement="toast"
                  onAction={() => void submit()}
                />
              )
            ) : null}
            <button className="vp-action-button" type="button" onClick={submit} disabled={submitting}>
              {submitting
                ? tr('common.processing')
                : state.action === 'cancel'
                  ? tr('action.cancelButton')
                  : state.action === 'update-card'
                    ? tr('action.saveButton')
                  : paymentRequiresAction
                    ? tr('action.authenticateButton')
                    : tr('action.payButton')}
            </button>
          </>
        ) : null}
      </section>
    </main>
  );
}

function cancellationState(view: SubscriptionActionView): CancellationState | undefined {
  if (view.subscription.status === 'CANCELED') {
    return { status: 'cancellation', outcome: 'canceled', view };
  }
  if (view.subscription.cancelAtPeriodEnd) {
    return { status: 'cancellation', outcome: 'scheduled', view };
  }
  return undefined;
}

async function pollInvoiceSettlement(
  token: string,
  initial: SubscriptionActionView,
): Promise<SubscriptionActionView> {
  let current = initial;
  for (let attempt = 0; attempt < 10 && current.invoice?.status !== 'PAID'; attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, 1_000));
    current = await fetchSubscriptionAction('pay-now', token);
  }
  return current;
}

function normalizeAction(value: string | null): SubscriptionActionKind | null {
  if (value === 'pay-now' || value === 'cancel' || value === 'update-card') {
    return value;
  }
  return null;
}

async function pollCardUpdate(token: string, updateId: string): Promise<SubscriptionActionView> {
  let last = await fetchSubscriptionCardUpdateStatus(token, updateId);
  for (let attempt = 0; attempt < 10; attempt += 1) {
    if (last.payment?.status === 'SUCCEEDED' || last.payment?.status === 'FAILED') {
      return last;
    }
    await delay(3000);
    last = await fetchSubscriptionCardUpdateStatus(token, updateId);
  }
  return last;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function makeUpdateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `card-update:${crypto.randomUUID()}`;
  }
  return `card-update:${Date.now()}:${Math.random().toString(36).slice(2)}`;
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat(currentLocaleTag(), { dateStyle: 'medium' }).format(new Date(value));
}

function subscriptionStatusLabel(status: SubscriptionActionView['subscription']['status']): string {
  const labels: Record<SubscriptionActionView['subscription']['status'], string> = {
    TRIALING: tr('action.trialing'),
    ACTIVE: tr('action.active'),
    PAST_DUE: tr('action.pastDue'),
    CANCELED: tr('action.canceled'),
  };
  return labels[status];
}

function invoiceStatusLabel(status: NonNullable<SubscriptionActionView['invoice']>['status']): string {
  const labels: Record<NonNullable<SubscriptionActionView['invoice']>['status'], string> = {
    PENDING: tr('action.pending'),
    PROCESSING: tr('action.processing'),
    AWAITING_ACTION: tr('action.awaitingAction'),
    PAID: tr('action.paid'),
    PAST_DUE: tr('action.pastDue'),
    IN_GRACE: tr('action.inGrace'),
    FAILED: tr('action.failed'),
    CANCELED: tr('action.canceled'),
  };
  return labels[status];
}
